/**
 * Event Schedule Utilities
 *
 * All times are venue-local. No timezone conversion for visitors.
 * Visitors are at the venue, so times are displayed as-is.
 */

import type { TFunction } from "i18next";

export type ScheduleType = "single" | "daily" | "weekly" | "permanent";

export type EventStatus =
  | "happening-now"
  | "today-upcoming"
  | "today-past"
  | "upcoming"
  | "past";

export interface EventOccurrence {
  date: string; // ISO date YYYY-MM-DD
  startTime: string; // "14:00" or "14:00:00"
  endTime: string; // "15:30" or "15:30:00"
  status: EventStatus;
}

export interface EventForSchedule {
  id: string;
  scheduleType: ScheduleType;
  startDate: string | null; // YYYY-MM-DD
  endDate: string | null; // YYYY-MM-DD
  startTime: string; // "14:00:00"
  endTime: string; // "15:30:00"
  weeklyDays: number[] | null; // [0,6] for Sun/Sat
}

// =============================================================================
// Time Parsing Helpers
// =============================================================================

/**
 * Parse time string to minutes since midnight
 * Handles both "HH:MM" and "HH:MM:SS" formats
 */
function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Get current time as minutes since midnight
 */
function getCurrentMinutes(now: Date): number {
  return now.getHours() * 60 + now.getMinutes();
}

/**
 * Format date to YYYY-MM-DD using local time (not UTC)
 */
function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Get day of week (0=Sun, 6=Sat)
 */
function getDayOfWeek(date: Date): number {
  return date.getDay();
}

/**
 * Check if a date falls within a range (inclusive)
 */
function isDateInRange(
  date: string,
  startDate: string | null,
  endDate: string | null
): boolean {
  if (!startDate && !endDate) return true; // No bounds = always valid
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
}

// =============================================================================
// Core Functions
// =============================================================================

/**
 * Check if an event is happening right now
 */
export function isEventHappeningNow(
  event: EventForSchedule,
  now: Date = new Date()
): boolean {
  const today = formatDate(now);
  const currentMinutes = getCurrentMinutes(now);
  const startMinutes = parseTimeToMinutes(event.startTime);
  const endMinutes = parseTimeToMinutes(event.endTime);

  // Check if within time window
  const isWithinTime = currentMinutes >= startMinutes && currentMinutes < endMinutes;
  if (!isWithinTime) return false;

  // Check schedule type
  switch (event.scheduleType) {
    case "single":
      return event.startDate === today;

    case "daily":
      return isDateInRange(today, event.startDate, event.endDate);

    case "weekly":
      if (!event.weeklyDays?.length) return false;
      const dayOfWeek = getDayOfWeek(now);
      return (
        event.weeklyDays.includes(dayOfWeek) &&
        isDateInRange(today, event.startDate, event.endDate)
      );

    case "permanent":
      return true;

    default:
      return false;
  }
}

/**
 * Get the status of an event
 */
export function getEventStatus(
  event: EventForSchedule,
  now: Date = new Date()
): EventStatus {
  const today = formatDate(now);
  const currentMinutes = getCurrentMinutes(now);
  const startMinutes = parseTimeToMinutes(event.startTime);
  const endMinutes = parseTimeToMinutes(event.endTime);

  // Check if event occurs today
  const occursToday = doesEventOccurOnDate(event, today);

  if (occursToday) {
    if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
      return "happening-now";
    }
    if (currentMinutes < startMinutes) {
      return "today-upcoming";
    }
    return "today-past";
  }

  // Check if event is in the future
  const nextOccurrence = getNextOccurrence(event, now);
  if (nextOccurrence) {
    return "upcoming";
  }

  return "past";
}

/**
 * Check if an event occurs on a specific date
 */
export function doesEventOccurOnDate(
  event: EventForSchedule,
  date: string
): boolean {
  switch (event.scheduleType) {
    case "single":
      return event.startDate === date;

    case "daily":
      return isDateInRange(date, event.startDate, event.endDate);

    case "weekly": {
      if (!event.weeklyDays?.length) return false;
      const dateObj = new Date(date + "T12:00:00"); // Noon to avoid TZ issues
      const dayOfWeek = dateObj.getDay();
      return (
        event.weeklyDays.includes(dayOfWeek) &&
        isDateInRange(date, event.startDate, event.endDate)
      );
    }

    case "permanent":
      return true;

    default:
      return false;
  }
}

/**
 * Get the next occurrence of an event
 */
export function getNextOccurrence(
  event: EventForSchedule,
  now: Date = new Date()
): EventOccurrence | null {
  const today = formatDate(now);
  const currentMinutes = getCurrentMinutes(now);
  const startMinutes = parseTimeToMinutes(event.startTime);

  // Check today first (if event hasn't started yet)
  if (doesEventOccurOnDate(event, today) && currentMinutes < startMinutes) {
    return {
      date: today,
      startTime: event.startTime,
      endTime: event.endTime,
      status: "today-upcoming",
    };
  }

  // Look ahead up to 365 days
  const maxDays = 365;
  const searchDate = new Date(now);

  for (let i = 1; i <= maxDays; i++) {
    searchDate.setDate(searchDate.getDate() + 1);
    const dateStr = formatDate(searchDate);

    // Stop if past end date for bounded events
    if (event.endDate && dateStr > event.endDate) {
      return null;
    }

    if (doesEventOccurOnDate(event, dateStr)) {
      return {
        date: dateStr,
        startTime: event.startTime,
        endTime: event.endTime,
        status: "upcoming",
      };
    }
  }

  return null;
}

/**
 * Get all occurrences of an event for today
 */
export function getTodaysOccurrences(
  event: EventForSchedule,
  now: Date = new Date()
): EventOccurrence[] {
  const today = formatDate(now);

  if (!doesEventOccurOnDate(event, today)) {
    return [];
  }

  const status = getEventStatus(event, now);

  return [
    {
      date: today,
      startTime: event.startTime,
      endTime: event.endTime,
      status,
    },
  ];
}

// =============================================================================
// Formatting Functions
// =============================================================================

/**
 * Format time for display.
 * With locale: uses Intl.DateTimeFormat (e.g., "2:00 PM" en, "14:00" fr)
 * Without locale: falls back to English AM/PM
 */
export function formatTime(time: string, locale?: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  if (locale) {
    const date = new Date(2000, 0, 1, hours, minutes);
    return date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  }
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return minutes === 0
    ? `${displayHours} ${period}`
    : `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Format event time range (e.g., "2:00 PM - 3:30 PM")
 */
export function formatEventTime(event: EventForSchedule, locale?: string): string {
  return `${formatTime(event.startTime, locale)} - ${formatTime(event.endTime, locale)}`;
}

/**
 * Format event schedule description
 */
export function formatEventSchedule(event: EventForSchedule, t?: TFunction): string {
  switch (event.scheduleType) {
    case "single":
      if (!event.startDate) return t ? t("events:schedule.dateTBD") : "Date TBD";
      return formatDateForDisplay(event.startDate, t);

    case "daily":
      if (event.startDate && event.endDate) {
        const start = formatDateForDisplay(event.startDate, t);
        const end = formatDateForDisplay(event.endDate, t);
        return t
          ? t("events:schedule.dailyRange", { start, end })
          : `Daily, ${start} - ${end}`;
      }
      if (event.startDate) {
        const start = formatDateForDisplay(event.startDate, t);
        return t
          ? t("events:schedule.dailyFrom", { start })
          : `Daily from ${start}`;
      }
      return t ? t("events:schedule.daily") : "Daily";

    case "weekly": {
      const days = formatWeeklyDays(event.weeklyDays, t);
      if (event.startDate && event.endDate) {
        const start = formatDateForDisplay(event.startDate, t);
        const end = formatDateForDisplay(event.endDate, t);
        return `${days}, ${start} - ${end}`;
      }
      return days;
    }

    case "permanent":
      return t ? t("events:schedule.ongoing") : "Ongoing";

    default:
      return "";
  }
}

/**
 * Format weekly days (e.g., "Saturdays & Sundays" or "Mon, Wed, Fri")
 */
function formatWeeklyDays(days: number[] | null, t?: TFunction): string {
  if (!days?.length) return "";

  const shortDay = (d: number) => t ? t(`pois:days.short.${d}`) : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d];
  const fullDay = (d: number) => t ? t(`pois:days.full.${d}`) : ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"][d];

  const sorted = [...days].sort((a, b) => a - b);

  // Special cases for weekend/weekday
  if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6) {
    return t ? t("events:schedule.weekends") : "Weekends";
  }
  if (
    sorted.length === 5 &&
    sorted.every((d, i) => d === i + 1)
  ) {
    return t ? t("events:schedule.weekdays") : "Weekdays";
  }
  if (sorted.length === 7) {
    return t ? t("events:schedule.everyDay") : "Every day";
  }

  if (sorted.length === 1) {
    return fullDay(sorted[0]);
  }

  if (sorted.length === 2) {
    return `${fullDay(sorted[0])} & ${fullDay(sorted[1])}`;
  }

  return sorted.map((d) => shortDay(d)).join(", ");
}

/**
 * Format date for display (e.g., "Aug 15" or "Aug 15, 2025")
 */
function formatDateForDisplay(dateStr: string, t?: TFunction): string {
  const date = new Date(dateStr + "T12:00:00");
  const now = new Date();
  const currentYear = now.getFullYear();
  const eventYear = date.getFullYear();

  const month = t ? t(`pois:months.${date.getMonth()}`) : date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();

  if (eventYear === currentYear) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${eventYear}`;
}

/**
 * Format "until" time for happening now events
 */
export function formatUntilTime(endTime: string, t?: TFunction, locale?: string): string {
  const time = formatTime(endTime, locale);
  return t ? t("events:schedule.until", { time }) : `Until ${time}`;
}

/**
 * Get relative date label (e.g., "Today", "Tomorrow", "Sat, Aug 17")
 */
export function getRelativeDateLabel(dateStr: string, now: Date = new Date(), t?: TFunction): string {
  const today = formatDate(now);

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = formatDate(tomorrow);

  if (dateStr === today) return t ? t("events:schedule.today") : "Today";
  if (dateStr === tomorrowStr) return t ? t("events:schedule.tomorrow") : "Tomorrow";

  const date = new Date(dateStr + "T12:00:00");
  const dayName = t ? t(`pois:days.short.${date.getDay()}`) : date.toLocaleString("en-US", { weekday: "short" });
  return `${dayName}, ${formatDateForDisplay(dateStr, t)}`;
}
